import { useState } from "react";
import { Button } from "../../../components/Button";
import MaterialTable from "./MaterialTable";
import { SalesAidModal } from "../sales-aid-modal/SalesAidModal";
import { api } from "../../../services/api";
import { useQuery } from "@tanstack/react-query";
import { DeleteFileConfirmModal } from "./DeleteFileConfirmModal";
import { ShowToast } from "../../../services/toast";
import RenameFileModal from "./RenameFileModal";
import { userRoles } from "../../../constant";
import { useUser } from "../../../services/user-context";
import React from "react";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import CohortAssignmentModal from "./CohortAssignmentModal";
import { DocumentPreviewModal } from "./DocumentPreviewModal";

const SALES_AID_FILES_QUERY_KEY = ["sales-aid-files-practice-materials"] as const;

function PraxisPlayPracticeMaterialsScreenView() {
    const { userInfo } = useUser() as any;
    const role = userInfo?.role;
    const [showAddPracticeMaterialsModal, setShowAddPracticeMaterialsModal] = useState(false);

    const [isDeleteFileConfirmModalOpen, setIsDeleteFileConfirmModalOpen] = useState(false);
    const [deleteFile, setDeleteFile] = useState<any>(null);
    const [deleteFileLoading, setDeleteFileLoading] = useState(false);
    const [editFile, setEditFile] = useState<any>(null);
    const [cohortAssignmentFile, setCohortAssignmentFile] = useState<any>(null);
    const [previewFile, setPreviewFile] = useState<any>(null);

    const isAdmin = role === userRoles.Admin;
    const { data, isLoading, error, refetch: refetchSalesAidFiles } = useQuery({
        queryKey: [...SALES_AID_FILES_QUERY_KEY, isAdmin ? "management" : "user"],
        queryFn: () => isAdmin ? api.getSalesAidManagementFiles() : api.getSalesAidFiles(),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    const salesAidFiles = data?.data ?? [];

    const handleDeleteFile = (file: any) => {
        setDeleteFile(file);
        setIsDeleteFileConfirmModalOpen(true);
    }
    const handleDeleteFileConfirm = async () => {
        try {
            setDeleteFileLoading(true);
            // delete file
            const res = await api.deleteSalesAidFile(deleteFile.id);
            if (res.status === 200) {
                refetchSalesAidFiles();
                setIsDeleteFileConfirmModalOpen(false);
                setDeleteFile(null);
                ShowToast({
                    message: "File deleted successfully",
                    type: "success"
                })
            }
            setDeleteFileLoading(false);
        } catch (error) {
            setDeleteFileLoading(false);
            setDeleteFile(null);
        }
    }
    const handleEditFile = (file: any) => {
        setEditFile(file);
    }
    const onAddPracticeMaterials = () => {
        setShowAddPracticeMaterialsModal(true);
    }

    const handleCohortAssignment = (file: any) => {
        setCohortAssignmentFile(file);
    }

    const handlePreview = (file: any) => {
        setPreviewFile(file);
    }

    return (
        <div className="md:self-center md:flex-1 rounded-2xl tall2:h-[calc(100vh-120px)] h-auto overflow-auto w-full tall2:mt-3 mt-4 flex-col justify-start items-start tall2:gap-2 gap-6 flex">
            <div className=" bg-white rounded-2xl shadow flex-col justify-start items-start w-full">
                <div className="w-full flex-1 flex-col justify-start items-center gap-7 flex">
                    <div className="w-full flex flex-col gap-2">
                        <MaterialTable
                            isAdmin={isAdmin}
                            onDelete={handleDeleteFile}
                            data={salesAidFiles}
                            isLoading={isLoading}
                            error={error}
                            onEdit={handleEditFile}
                            onCohortAssignment={handleCohortAssignment}
                            onPreview={handlePreview}
                        />
                    </div>
                    <SalesAidModal
                        isOpen={showAddPracticeMaterialsModal}
                        onClose={() => setShowAddPracticeMaterialsModal(false)}
                        onSuccess={() => {
                            setShowAddPracticeMaterialsModal(false);
                            refetchSalesAidFiles();
                        }}
                    />

                    <DeleteFileConfirmModal
                        isOpen={isDeleteFileConfirmModalOpen}
                        onClose={() => setIsDeleteFileConfirmModalOpen(false)}
                        onConfirm={handleDeleteFileConfirm}
                        loading={deleteFileLoading}
                        file={deleteFile}
                    />
                    {editFile && <RenameFileModal isOpen={!!editFile} onClose={() => setEditFile(null)} file={editFile} onSuccess={() => {
                        setEditFile(null);
                        refetchSalesAidFiles();
                    }} />}
                    {
                        cohortAssignmentFile &&
                        <CohortAssignmentModal
                            isOpen={!!cohortAssignmentFile}
                            onClose={() => setCohortAssignmentFile(null)}
                            file={cohortAssignmentFile}
                            onSuccess={() => {
                                setCohortAssignmentFile(null);
                                refetchSalesAidFiles();
                            }}
                        />
                    }
                    {
                        previewFile &&
                        <DocumentPreviewModal
                            isOpen={!!previewFile}
                            onClose={() => setPreviewFile(null)}
                            file={previewFile}
                        />
                    }
                </div>
            </div>

            {isAdmin && <div className="w-full flex justify-start px-4">
                <Button
                    variant="primary"
                    className="!h-10 !text-[#383838] !bg-lightGray hover:!opacity-80 !rounded-full"
                    onClick={onAddPracticeMaterials}
                >
                    Add Documents <PlusIcon className="w-4 h-4 [&_path]:stroke-[#383838]" />
                </Button>
            </div>}
        </div>
    );
}

export const PraxisPlayPracticeMaterialsScreen = React.memo(PraxisPlayPracticeMaterialsScreenView);